/**
 * Bounding box of the verified tag's geometry.
 *
 * WHY THIS EXISTS — the shared `getBBBox()` returns an empty box for our nodes.
 *
 * Measured live on task `1HDD06SV0147` (dbId 96 = `HDD06SV0147`, model
 * `…CAD_Export_Translated.nwd`, 160 nodes, correctly isolated on screen):
 *
 *   enumNodeChildren(96, cb, false)   -> [102]        direct child, correct
 *   enumNodeChildren(96, cb, true)    -> [96]         recursion does NOT descend
 *   enumNodeFragments(96, cb, false)  -> []           no fragment on 96 itself
 *   enumNodeFragments(96, cb, true)   -> [0]          the fragment, owned by dbId 104
 *
 * `getBBBox` (`src/utils/forgeUtils.js:49`) walks children with `recursive=true`
 * — which in this viewer build yields only the node itself — and then resolves
 * fragments with `recursive=false` via `getFragIdFromDbId`. Two traversal
 * defects that compound: neither step can reach dbId 104, so zero fragments are
 * found, `getModifiedWorldBoundingBox` unions nothing, and the caller receives a
 * Box3 whose `.isEmpty()` is true. The failure is silent apart from one
 * `Failed to get the center for IDs::` debug line.
 *
 * This module skips the child traversal entirely: fragments are enumerated
 * recursively in a single call, which is the only step empirically proven to
 * reach the geometry.
 *
 * SECOND DEFECT THIS GUARDS — the dbIds may not belong to the current model.
 * Two models are loaded on this route: the NWD design model, and a 1-node `{3D}`
 * model. `viewer.model` points at whichever is current, and there is a real
 * window during load where that is the empty one — where `dbId 96` exists but
 * owns nothing, producing the identical empty box. dbIds are per-model and are
 * not portable between them, so inheriting `viewer.model` is not safe. Instead
 * every loaded model is probed and the one whose tree actually yields fragments
 * wins. That also removes the dependency on *when* the caller runs.
 *
 * NOTE — `getBBBox`/`getMiddleBBoxForGroup` are shared utilities with this same
 * defect for any node whose fragments sit below a child node. Fixing them is out
 * of scope for the AVA-475 spike (blast radius across the whole app); this stays
 * feature-local by design. See `.claude/features/AVA-475_SPIKE_FINDINGS.md`.
 */
import { log as debugLog, warn as debugWarn } from '@/utils/debugLogger';
import { buildUnitTransform } from '@/features/Verification/utils/modelUnits';

/**
 * Every model currently loaded in the viewer, newest API first.
 * Falls back progressively so a viewer build without `modelQueue` still works.
 */
export function listLoadedModels(viewer) {
  if (!viewer) return [];
  const candidates =
    viewer.getVisibleModels?.() ||
    viewer.impl?.modelQueue?.()?.getModels?.() ||
    (viewer.model ? [viewer.model] : []);
  return Array.isArray(candidates) ? candidates.filter(Boolean) : [];
}

/**
 * Fragment ids for `dbIds`, enumerated RECURSIVELY.
 *
 * The recursion is the whole point — see the file header. A non-recursive walk
 * returns [] for exactly the nodes we care about.
 */
export function collectFragIds(tree, dbIds) {
  const fragIds = [];
  if (!tree || typeof tree.enumNodeFragments !== 'function') return fragIds;
  for (const dbId of dbIds) {
    try {
      tree.enumNodeFragments(dbId, (fragId) => fragIds.push(fragId), true);
    } catch (err) {
      debugWarn('[verifiedBBox] enumNodeFragments threw', { dbId, err });
    }
  }
  return fragIds;
}

/** World-space Box3 spanning `fragIds`, or null when none resolve to bounds. */
function buildBox(model, fragIds) {
  const THREE = window.THREE;
  if (!THREE || !fragIds.length) return null;
  const fragList = model.getFragmentList?.();
  if (!fragList) return null;

  const box = new THREE.Box3();
  const fragBox = new THREE.Box3();
  for (const fragId of fragIds) {
    try {
      fragList.getWorldBounds(fragId, fragBox);
      box.union(fragBox);
    } catch (err) {
      debugWarn('[verifiedBBox] getWorldBounds threw', { fragId, err });
    }
  }
  return box.isEmpty() ? null : box;
}

/** Raw box dimensions, in whatever units the owning model states them. */
function sizeViewer(box) {
  const THREE = window.THREE;
  const size = box.getSize(new THREE.Vector3());
  return { x: size.x, y: size.y, z: size.z };
}

/**
 * Box dimensions in millimetres — the unit a surveyor reasons in.
 *
 * The scale is per-MODEL and passed in, because that is the only thing that
 * knows it: this box came out of one specific model's fragments, and the two
 * models on this route need not agree about what a unit is. NULL when the scale
 * is unknown, never a number: `size * null` is 0, and a `0 x 0 x 0 mm` support
 * in a log reads as missing geometry rather than as a missing scale.
 *
 * @param {object} box THREE.Box3 in that model's units.
 * @param {number|null} mmPerViewerUnit From `buildUnitTransform`.
 * @returns {{x:number,y:number,z:number}|null} Millimetres, rounded.
 */
function sizeMm(box, mmPerViewerUnit) {
  if (!Number.isFinite(mmPerViewerUnit) || mmPerViewerUnit <= 0) return null;
  const size = sizeViewer(box);
  return {
    x: Math.round(size.x * mmPerViewerUnit),
    y: Math.round(size.y * mmPerViewerUnit),
    z: Math.round(size.z * mmPerViewerUnit),
  };
}

/**
 * Resolve the verified tag's bounding box against whichever loaded model
 * actually owns the geometry.
 *
 * @param {object} viewer  Autodesk viewer instance.
 * @param {number[]} dbIds Verified tag dbIds.
 * @returns {{ bbox: object, model: object, modelId: number, mmPerViewerUnit: number|null,
 *   fragCount: number } | null} null when no loaded model yields fragments for
 *   these dbIds. `model` is the one the feature's unit transform is built from
 *   — this function choosing it is what stops anything downstream guessing.
 */
export function resolveVerifiedBBox(viewer, dbIds) {
  if (!viewer || !Array.isArray(dbIds) || !dbIds.length) {
    debugLog('[verifiedBBox] skipped — no viewer or no dbIds', {
      hasViewer: !!viewer,
      dbIdCount: dbIds?.length ?? 0,
    });
    return null;
  }

  const models = listLoadedModels(viewer);
  const attempts = [];
  let best = null;

  for (const model of models) {
    const tree = model.getInstanceTree?.();
    // Per-model, because units are a property of a MODEL and two are loaded
    // here. Null when this model cannot state its units — the empty `{3D}`
    // model is exactly that case, and it is also the one that yields no
    // fragments, so it loses on both counts.
    const mmPerViewerUnit = buildUnitTransform(model)?.mmPerViewerUnit ?? null;
    const attempt = {
      modelId: model.id,
      isCurrent: model === viewer.model,
      hasTree: !!tree,
      mmPerViewerUnit,
      fragCount: 0,
      boxed: false,
    };

    if (tree) {
      const fragIds = collectFragIds(tree, dbIds);
      attempt.fragCount = fragIds.length;
      const box = buildBox(model, fragIds);
      if (box) {
        attempt.boxed = true;
        // Both spaces, so the log discloses which one it is speaking in — a
        // bare size that could be either is how a millimetre model reads as an
        // 850-metre post and nobody notices.
        attempt.sizeViewer = sizeViewer(box);
        attempt.sizeMm = sizeMm(box, mmPerViewerUnit);
        // Most fragments wins — a partial match on the wrong model should never
        // beat a full match on the right one.
        if (!best || fragIds.length > best.fragCount) {
          best = {
            bbox: box,
            model,
            modelId: model.id,
            mmPerViewerUnit,
            fragCount: fragIds.length,
            // Identifies WHICH tag this box belongs to. Callers cache the result
            // and need to tell "same answer again" from "different tag, same
            // fragment count" — modelId + fragCount alone cannot.
            dbIdKey: dbIds.join(','),
          };
        }
      }
    }
    attempts.push(attempt);
  }

  if (!best) {
    debugWarn('[verifiedBBox] NO model yielded geometry for these dbIds', {
      dbIds,
      modelCount: models.length,
      attempts,
    });
    return null;
  }

  debugLog('[verifiedBBox] resolved', {
    dbIds,
    chosenModelId: best.modelId,
    chosenIsCurrent: best.model === viewer.model,
    fragCount: best.fragCount,
    mmPerViewerUnit: best.mmPerViewerUnit,
    sizeViewer: sizeViewer(best.bbox),
    sizeMm: sizeMm(best.bbox, best.mmPerViewerUnit),
    center: best.bbox.getCenter(new window.THREE.Vector3()),
    attempts,
  });
  return best;
}

export default resolveVerifiedBBox;
